import { faArrowDown, faArrowUp, faMinus } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { globalStateCTX } from '../../GlobalState/GlobalState';
import { useNonce } from '../../Utils/hooks';
import { formatCurrency, getComparedPercentage } from '../../Utils/number';
import NumberFormater from '../NumberFormater/NumberFormater';
import KpiCard, { KPIStatus } from './KPICard/KpiCard';
import colors from '../../Utils/Colors/Colors.module.scss';
import { YTQStatisticsReadView } from '../../GeneratedServices';
import { statisticsApi } from '../../Http/Http';
import { useTranslation } from 'react-i18next';
import { FilterValuesType } from '../../FiltersProvider/FiltersProvider';

interface Props {
  filterValues?: FilterValuesType;
  propertyId?: number;
}
const YearlyRollingIncomeKpi: React.FC<Props> = (props) => {
  const { t } = useTranslation('common');

  const { handleHttpErrors } = React.useContext(globalStateCTX);

  const [yearlyIncomeStatistics, setYearlyIncomeStatistics] = React.useState<YTQStatisticsReadView>();
  const [status, setStatus] = React.useState<KPIStatus>(KPIStatus.Loading);
  const [shouldReload, reload] = useNonce();

  React.useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      try {
        setStatus(KPIStatus.Loading);
        const { data } = await statisticsApi.statisticsResourceReadIncomeYTQStatistics(
          {
            ...props.filterValues,
            propertyId: props.propertyId
          },
          {
            signal: abortController.signal
          }
        );

        setYearlyIncomeStatistics(data);
        setStatus(KPIStatus.None);
      } catch (error) {
        handleHttpErrors(error) && setStatus(KPIStatus.LoadError);
      }
    };
    load();

    return () => abortController.abort();
  }, [props.filterValues, props.propertyId, handleHttpErrors, shouldReload]);

  const formattedValue = yearlyIncomeStatistics
    ? formatCurrency(yearlyIncomeStatistics?.value, { forceCurrency: 'KSEK' })
    : undefined;
  const comparedIncome = yearlyIncomeStatistics
    ? getComparedPercentage(yearlyIncomeStatistics.value, yearlyIncomeStatistics.businessPlanValue)
    : 0;
  const formattedValuePerArea = yearlyIncomeStatistics
    ? formatCurrency(yearlyIncomeStatistics?.valuePerArea, { forceCurrency: 'SEK', unit: t('sekSqm') })
    : undefined;

  const hasBussinesPlanValue = yearlyIncomeStatistics?.businessPlanValue;
  return (
    <KpiCard
      title={t('yearlyRollingIncome')}
      value={<NumberFormater value={formattedValue?.value} additionalProps={{ decimalScale: 0 }} />}
      valueSuffix={formattedValue?.suffix}
      additionalValue={<NumberFormater value={formattedValuePerArea?.value} additionalProps={{ decimalScale: 0 }} />}
      additionalValueSuffix={formattedValuePerArea?.suffix}
      extra={
        hasBussinesPlanValue ? (
          <NumberFormater value={comparedIncome} suffix={t('vsBusinessPlan')} />
        ) : (
          t('noBusinessPlanInformation')
        )
      }
      extraColor={
        hasBussinesPlanValue ? (comparedIncome && comparedIncome > 0 ? colors.green : colors.red) : colors.grey
      }
      extraIcon={hasBussinesPlanValue ? (comparedIncome && comparedIncome > 0 ? faArrowUp : faArrowDown) : faMinus}
      includeExtra
      status={status}
      retry={reload}
    />
  );
};

export default YearlyRollingIncomeKpi;
